// Live EPSE Simulation Hook
// Real-time simulation with debounced updates

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { BESSConfig, SimulationKPIs, LiveChartPoint } from '@/types/epse';
import { SyntheticQuarterData } from '@/lib/epseSyntheticProfile';

interface LiveSimulationResult {
  kpis: SimulationKPIs;
  chartData: LiveChartPoint[];
  isCalculating: boolean;
}

/**
 * Run a quick simulation on a subset of data for live updates
 * If bessConfig is null, skip battery logic and just show load vs limit
 */
function simulateLive(
  profileData: number[],
  bessConfig: BESSConfig | null,
  connectionLimit: number
): { kpis: Partial<SimulationKPIs>; chartData: LiveChartPoint[] } {
  const chartData: LiveChartPoint[] = [];
  
  let peakBefore = 0;
  let peakAfter = 0;
  let totalCharge = 0;
  let totalDischarge = 0;
  let peakOverruns = 0;
  let energyAboveLimit = 0;

  // If BESS is disabled, just show load data without battery
  if (!bessConfig) {
    profileData.forEach((load, i) => {
      const hour = Math.floor((i % 96) / 4);
      
      peakBefore = Math.max(peakBefore, load);
      peakAfter = Math.max(peakAfter, load);
      
      let shortfall_kw = 0;
      if (load > connectionLimit) {
        shortfall_kw = load - connectionLimit;
        peakOverruns++;
        energyAboveLimit += shortfall_kw * 0.25;
      }

      chartData.push({
        quarter: i % 96,
        time: hour.toString(),
        load,
        batteryAction: 0,
        netLoad: load,
        soc: 0,
        socPercent: 0,
        shortfall_kw,
      });
    });

    return {
      kpis: {
        peak_reduction_kw: 0,
        peak_reduction_percent: 0,
        peak_overruns_count: peakOverruns,
        peak_overruns_duration_hours: peakOverruns * 0.25,
        energy_above_limit_kwh: energyAboveLimit,
        bess_cycles: 0,
        bess_throughput_kwh: 0,
      },
      chartData,
    };
  }

  // BESS is enabled - run full simulation
  const {
    capacity_kwh,
    power_kw,
    efficiency_charge,
    efficiency_discharge,
    soc_min,
    soc_max,
    soc_initial,
  } = bessConfig;

  let soc = soc_initial * capacity_kwh;
  const socMin = soc_min * capacity_kwh;
  const socMax = soc_max * capacity_kwh;

  profileData.forEach((load, i) => {
    const hour = Math.floor((i % 96) / 4);
    
    peakBefore = Math.max(peakBefore, load);
    
    let batteryAction = 0;
    let netLoad = load;
    let shortfall_kw = 0;

    if (load > connectionLimit) {
      // Need to discharge
      const excess = load - connectionLimit;
      const maxDischarge = Math.min(
        power_kw,
        (soc - socMin) / efficiency_discharge
      );
      batteryAction = -Math.min(excess, maxDischarge);
      soc += batteryAction * efficiency_discharge;
      totalDischarge += Math.abs(batteryAction) * efficiency_discharge * 0.25;
      netLoad = load + batteryAction;
      
      // Calculate shortfall (remaining excess after battery discharge)
      if (netLoad > connectionLimit) {
        shortfall_kw = netLoad - connectionLimit;
        peakOverruns++;
        energyAboveLimit += shortfall_kw * 0.25;
      }
    } else if (soc < socMax) {
      // Can charge
      const headroom = connectionLimit - load;
      const maxCharge = Math.min(
        power_kw,
        (socMax - soc) * efficiency_charge,
        headroom * 0.8 // Leave some headroom
      );
      if (maxCharge > 0) {
        batteryAction = maxCharge;
        soc += batteryAction;
        totalCharge += batteryAction * 0.25;
        netLoad = load + batteryAction / efficiency_charge;
      }
    }

    peakAfter = Math.max(peakAfter, netLoad);

    chartData.push({
      quarter: i % 96,
      time: hour.toString(),
      load,
      batteryAction,
      netLoad,
      soc,
      socPercent: (soc / capacity_kwh) * 100,
      shortfall_kw,
    });
  });

  const cycles = (totalCharge + totalDischarge) / (2 * capacity_kwh);

  return {
    kpis: {
      peak_reduction_kw: peakBefore - peakAfter,
      peak_reduction_percent: peakBefore > 0 ? ((peakBefore - peakAfter) / peakBefore) * 100 : 0,
      peak_overruns_count: peakOverruns,
      peak_overruns_duration_hours: peakOverruns * 0.25,
      energy_above_limit_kwh: energyAboveLimit,
      bess_cycles: cycles,
      bess_throughput_kwh: totalCharge + totalDischarge,
    },
    chartData,
  };
}

export function useEPSELiveSimulation(
  profileData: number[] | SyntheticQuarterData[] | null,
  bessConfig: BESSConfig | null, // Can be null when BESS is disabled
  connectionLimit: number
): LiveSimulationResult {
  const [result, setResult] = useState<LiveSimulationResult>({
    kpis: {
      import_kwh: 0,
      export_kwh: 0,
      self_consumed_pv_kwh: 0,
      curtailed_pv_kwh: 0,
      peak_overruns_count: 0,
      peak_overruns_duration_hours: 0,
      energy_above_limit_kwh: 0,
      bess_cycles: 0,
      bess_throughput_kwh: 0,
      peak_reduction_kw: 0,
      peak_reduction_percent: 0,
    },
    chartData: [],
    isCalculating: false,
  });

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Normalize profile data to simple number array
  const normalizedData = useMemo(() => {
    if (!profileData || profileData.length === 0) return null;
    
    // Check if it's SyntheticQuarterData or simple numbers
    if (typeof profileData[0] === 'number') {
      return profileData as number[];
    }
    
    return (profileData as SyntheticQuarterData[]).map(d => d.kw_value);
  }, [profileData]);

  // Debounced simulation
  const runSimulation = useCallback(() => {
    if (!normalizedData || normalizedData.length === 0) return;

    setResult(prev => ({ ...prev, isCalculating: true }));

    // Use only first day (96 quarters) for live preview, or week if available
    const dataSlice = normalizedData.slice(0, Math.min(normalizedData.length, 96 * 7));

    const { kpis, chartData } = simulateLive(dataSlice, bessConfig, connectionLimit);

    setResult({
      kpis: {
        import_kwh: 0,
        export_kwh: 0,
        self_consumed_pv_kwh: 0,
        curtailed_pv_kwh: 0,
        peak_overruns_count: kpis.peak_overruns_count ?? 0,
        peak_overruns_duration_hours: kpis.peak_overruns_duration_hours ?? 0,
        energy_above_limit_kwh: kpis.energy_above_limit_kwh ?? 0,
        bess_cycles: kpis.bess_cycles ?? 0,
        bess_throughput_kwh: kpis.bess_throughput_kwh ?? 0,
        peak_reduction_kw: kpis.peak_reduction_kw ?? 0,
        peak_reduction_percent: kpis.peak_reduction_percent ?? 0,
      },
      chartData: chartData.slice(0, 96), // Only show first day in chart
      isCalculating: false,
    });
  }, [normalizedData, bessConfig, connectionLimit]);

  // Debounce effect
  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(runSimulation, 150);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [runSimulation]);

  return result;
}
